<template>
  <div class="min-h-screen flex justify-center bg-gray-50 dark:bg-gray-950 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-4">
      <div>
        <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {{ t('app.views.auth.register.title') }}
        </h2>
      </div>
      <form
        class="mt-8 space-y-6"
        @submit.prevent="handleRegister"
      >
        <div class="rounded-md shadow-sm -space-y-px">
          <div>
            <label
              for="full_name"
              class="sr-only"
            >{{ t('app.views.auth.register.full_name_placeholder') }}</label>
            <input
              id="full_name"
              v-model="fullName"
              name="full_name"
              type="text"
              required
              class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              :placeholder="t('app.views.auth.register.full_name_placeholder')"
            >
          </div>
          <div>
            <label
              for="email-address"
              class="sr-only"
            >{{ t('app.views.auth.register.email_placeholder') }}</label>
            <input
              id="email-address"
              v-model="email"
              name="email"
              type="email"
              autocomplete="email"
              required
              class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              :placeholder="t('app.views.auth.register.email_placeholder')"
            >
          </div>
          <div>
            <label
              for="password"
              class="sr-only"
            >{{ t('app.views.auth.register.password_placeholder') }}</label>
            <input
              id="password"
              v-model="password"
              name="password"
              type="password"
              autocomplete="new-password"
              required
              class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              :placeholder="t('app.views.auth.register.password_placeholder')"
            >
          </div>
          <div>
            <label
              for="confirm-password"
              class="sr-only"
            >{{ t('app.views.auth.register.confirm_password_placeholder') }}</label>
            <input
              id="confirm-password"
              v-model="confirmPassword"
              name="confirm_password"
              type="password"
              autocomplete="new-password"
              required
              class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              :placeholder="t('app.views.auth.register.confirm_password_placeholder')"
            >
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div class="text-sm">
            <router-link
              to="/login"
              class="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {{ t('app.views.auth.register.login_prompt') }}
            </router-link>
          </div>
        </div>

        <div>
          <button
            type="submit"
            class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            :disabled="loading || !passwordsMatch"
          >
            <span v-if="!loading">{{ t('app.views.auth.register.cta') }}</span>
            <span v-else>{{ t('app.views.auth.register.cta_loading') }}</span>
          </button>
        </div>
        
        <div
          v-if="error"
          class="text-red-500 text-sm text-center"
        >
          {{ error || t('app.views.auth.register.errors.failed') }}
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';

const fullName = ref('');
const email = ref('');
const password = ref('');
const confirmPassword = ref('');
const error = ref('');
const loading = ref(false);
const router = useRouter();
const authStore = useAuthStore();
const { t } = useI18n();

const passwordsMatch = computed(() => 
  password.value === confirmPassword.value && password.value.length > 0
);

const handleRegister = async () => {
  if (!passwordsMatch.value) return;
  
  try {
    error.value = '';
    loading.value = true;
    
    const success = await authStore.register({
      email: email.value,
      password: password.value,
      full_name: fullName.value
    });
    
    if (success) {
      // authStore.register() calls login() which handles navigation based on user role
      // No need to navigate here
    } else {
      error.value = authStore.error || (t('app.views.auth.register.errors.failed') as string);
    }
  } catch (err) {
    error.value = t('app.views.auth.register.errors.generic') as string;
    console.error('Registration error:', err);
  } finally {
    loading.value = false;
  }
};
</script>
